import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import config from '../config';
import './ActivityPanel.css';

const ActivityPanel = ({ 
  currentProspect = null, 
  isExpanded = false, 
  onExpand, 
  onClose,
  canvasMode = null,
  canvasFilter = null
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [activityItems, setActivityItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const startTimeRef = useRef(Date.now());

  // Build activities from current prospect
  const activities = useMemo(() => {
    const buildInitials = (name) => {
      if (!name) return 'PR';
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length === 0) return 'PR';
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    };

    const primary = currentProspect
      ? {
          id: 'current-prospect',
          when: 'Now',
          what: currentProspect.current_activity || 'Outreach in progress',
          who: {
            name: currentProspect.name || 'Prospect',
            title: currentProspect.title || currentProspect.position || '',
            company: currentProspect.company || '',
            avatarText: buildInitials(currentProspect.name),
            avatarUrl: currentProspect.image_url || currentProspect.imageUrl || currentProspect.avatar,
            email: currentProspect.email || '',
            linkedin: currentProspect.linkedin || currentProspect.linkedin_url || '',
          },
          where: currentProspect.company || 'Active account',
          location: currentProspect.location || currentProspect.city || currentProspect.state || '',
          agent: currentProspect.active_agent || 'Digital Twin',
          status: 'active',
          progress: typeof currentProspect.progress === 'number' ? currentProspect.progress : 72,
          prospect: currentProspect,
        }
      : null;

    return primary ? [primary] : [];
  }, [currentProspect]);

  // Elapsed time counter
  useEffect(() => {
    if (currentProspect) {
      const timer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentProspect]);

  // Fetch activity items when canvas mode changes
  useEffect(() => {
    if (canvasMode && canvasFilter) {
      setLoading(true);
      fetch(`${config.apiUrl}/api/activity-details?type=${canvasFilter}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem(config.tokenKey)}` }
      })
      .then(res => res.json())
      .then(data => {
        setActivityItems(data.items || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching activity details:', err);
        setLoading(false);
      });
    }
  }, [canvasMode, canvasFilter]);

  const currentActivity = activities[0];

  // ProspectImage component
  const ProspectImage = ({ who }) => {
    const [imageError, setImageError] = useState(false);

    const renderInitials = () => (
      <div className="prospect-avatar-initials">
        {who.avatarText}
      </div>
    );

    if (!who.avatarUrl || imageError) {
      return renderInitials();
    }

    return (
      <img
        src={who.avatarUrl}
        alt={who.name}
        onError={() => setImageError(true)}
        className="prospect-avatar-image"
      />
    );
  };

  return (
    <motion.div
      className={`activity-panel ${isExpanded ? 'expanded' : ''}`}
      initial={{ x: -400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -400, opacity: 0 }}
      transition={{ 
        type: "spring", 
        damping: 25, 
        stiffness: 300 
      }}
    >
      {/* Header */}
      <div className="activity-panel-header">
        <div className="header-title-section">
          <span className="panel-title">
            {isExpanded ? 'Prospect Details' : 'Activity Watch'}
          </span>
          <div className="activity-status-badge">
            <span className="status-dot active"></span>
            <span className="status-text">Live</span>
          </div>
        </div>
        
        <div className="header-actions">
          <button 
            className="expand-btn" 
            onClick={onExpand}
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              {isExpanded ? (
                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              ) : (
                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              )}
            </svg>
          </button>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Content based on canvas mode or expanded state */}
      {canvasMode ? (
        // Canvas mode - Show activity list
        <div className="activity-list-view">
          <div className="list-header">
            <button className="back-btn" onClick={() => window.dispatchEvent(new CustomEvent('exitCanvasMode'))}>
              ← Back
            </button>
            <span className="list-title">
              {canvasFilter === 'emails_sent' && 'Emails Sent'}
              {canvasFilter === 'opened' && 'Emails Opened'}
              {canvasFilter === 'responses' && 'Responses'}
              {canvasFilter === 'meetings' && 'Meetings Scheduled'}
            </span>
            <span className="list-count">{activityItems.length}</span>
          </div>
          <div className="list-items">
            {loading ? (
              <div className="loading-state">Loading...</div>
            ) : activityItems.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">📭</span>
                <span className="empty-text">No activity yet</span>
              </div>
            ) : (
              activityItems.map((item, idx) => (
                <div key={idx} className="activity-list-item">
                  <div className="item-avatar">
                    {item.name ? item.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : 'PR'}
                  </div>
                  <div className="item-details">
                    <div className="item-name">{item.name || 'Unknown'}</div>
                    <div className="item-meta">
                      <span className="item-company">{item.company}</span>
                      <span className="item-action">{item.action}</span>
                    </div>
                  </div>
                  <div className="item-time">{item.timestamp ? new Date(item.timestamp).toLocaleDateString() : ''}</div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : activities.length === 0 ? (
        // Empty state
        <div className="activity-watch-content">
          <div className="activity-carousel">
            <div className="activity-card-compact" style={{ textAlign: 'center', padding: '40px 24px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>👀</div>
              <div className="value" style={{ marginBottom: '8px' }}>No Active Prospects</div>
              <div className="sublabel">Start a campaign to see activity here</div>
            </div>
          </div>
        </div>
      ) : !isExpanded && currentActivity ? (
        // Collapsed state - Activity Watch carousel
        <div className="activity-watch-content">
          <div className="activity-carousel">
            <AnimatePresence mode="wait">
              <motion.div
                key="current-activity"
                className="activity-card-compact"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                <div className="activity-time-badge">
                  <span>🕐 {currentActivity.when}</span>
                </div>

                <div className="activity-info">
                  <div className="what-section">
                    <span className="label">What</span>
                    <span className="value">{currentActivity.what}</span>
                  </div>

                  <div className="who-section">
                    <span className="label">Who</span>
                    <div className="who-details">
                      <div className="who-avatar">
                        {currentActivity.who.avatarUrl ? (
                          <img src={currentActivity.who.avatarUrl} alt={currentActivity.who.name} />
                        ) : (
                          <span>{currentActivity.who.avatarText}</span>
                        )}
                      </div>
                      <div className="who-info">
                        <span className="name">{currentActivity.who.name}</span>
                        <span className="title">{currentActivity.who.title}</span>
                      </div>
                    </div>
                  </div>

                  <div className="where-section">
                    <span className="label">Where</span>
                    <span className="value">{currentActivity.where}</span>
                    {currentActivity.location && (
                      <span className="sublabel">{currentActivity.location}</span>
                    )}
                  </div>

                  <div className="progress-section">
                    <div className="activity-progress-bar">
                      <div 
                        className="activity-progress-fill"
                        style={{ width: `${currentActivity.progress}%` }}
                      />
                    </div>
                    <span className="activity-progress-text">{currentActivity.progress}%</span>
                  </div>
                </div>

                <div className="expand-hint">
                  <span>Click arrow to view details →</span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Quick Stats Section */}
          <div className="quick-stats-section">
            <div className="stat-card">
              <div className="stat-icon">⏱️</div>
              <div className="stat-content">
                <div className="stat-label">Time Elapsed</div>
                <div className="stat-value">{Math.floor(elapsedTime / 60)}m {elapsedTime % 60}s</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🎯</div>
              <div className="stat-content">
                <div className="stat-label">Next Action</div>
                <div className="stat-value">Follow-up</div>
              </div>
            </div>
          </div>

          {/* Mini Activity Timeline */}
          <div className="mini-timeline-section">
            <div className="timeline-header">
              <span className="timeline-title">Recent Activity</span>
            </div>
            <div className="timeline-items">
              <div className="timeline-item">
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <span className="timeline-action">Profile researched</span>
                  <span className="timeline-time">2m ago</span>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-dot completed"></div>
                <div className="timeline-content">
                  <span className="timeline-action">Email drafted</span>
                  <span className="timeline-time">5m ago</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : currentActivity ? (
        // Expanded state - Full prospect details
        <div className="prospect-details-content">
          <div className="prospect-header-section">
            <div className="prospect-image-container">
              <ProspectImage who={currentActivity.who} />
            </div>
            <div className="prospect-main-info">
              <h2 className="prospect-name">{currentActivity.who.name}</h2>
              <p className="prospect-title">{currentActivity.who.title}</p>
              {currentActivity.who.company && (
                <p className="prospect-company">{currentActivity.who.company}</p>
              )}
              {currentActivity.who.linkedin && (
                <a 
                  href={`https://linkedin.com${currentActivity.who.linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="linkedin-link"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                  View LinkedIn Profile
                </a>
              )}
            </div>
          </div>

          {/* Contact Information */}
          {(currentActivity.who.email || currentActivity.location) && (
            <div className="contact-section">
              <h3>Contact Information</h3>
              {currentActivity.who.email && (
                <div className="contact-row">
                  <span className="contact-label">Email</span>
                  <span className="contact-value">{currentActivity.who.email}</span>
                </div>
              )}
              {currentActivity.location && (
                <div className="contact-row">
                  <span className="contact-label">Location</span>
                  <span className="contact-value">
                    {currentActivity.where}, {currentActivity.location}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Activity Progress */}
          <div className="activity-progress-section">
            <h3>Current Activity</h3>
            <div className="activity-detail-card">
              <div className="activity-header">
                <span className="activity-name">{currentActivity.what}</span>
                <span className={`activity-status ${currentActivity.status}`}>
                  {currentActivity.status}
                </span>
              </div>
              <div className="activity-progress-container">
                <div className="activity-progress-info">
                  <span>Progress</span>
                  <span>{currentActivity.progress}%</span>
                </div>
                <div className="activity-progress-bar-large">
                  <div 
                    className="activity-progress-fill-large"
                    style={{ width: `${currentActivity.progress}%` }}
                  />
                </div>
              </div>
              <div className="agent-info">
                <span>Handled by:</span>
                <span className="agent-name">{currentActivity.agent}</span>
              </div>
            </div>
          </div>

          {/* Additional prospect data if available */}
          {currentActivity.prospect && (
            <>
              {/* Highlights */}
              {(currentActivity.prospect.highlights || currentActivity.prospect.reasons) && (
                <div className="highlights-section">
                  <h3>Why This Prospect?</h3>
                  <ul className="highlights-list">
                    {(currentActivity.prospect.highlights || currentActivity.prospect.reasons || []).map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Score if available */}
              {currentActivity.prospect.score && (
                <div className="score-section">
                  <h3>Fit Score</h3>
                  <div className="score-display">
                    <div className="score-value">{currentActivity.prospect.score}/100</div>
                    <div className="score-bar">
                      <div 
                        className="score-fill"
                        style={{ width: `${currentActivity.prospect.score}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : null}
    </motion.div>
  );
};

export default ActivityPanel;
